/**
 * Dropdown Options Configuration
 *
 * Centralized definitions for all dropdown options used throughout the application.
 * This consolidates all dropdown data for easy maintenance and consistency.
 */

/**
 * Standard dropdown option interface
 */
export interface DropdownOption {
  /** The value used internally */
  value: string;
  /** Display label shown to users */
  label: string;
  /** Tooltip description for the option */
  tooltip?: string;
}

// =============================================================================
// Object Type Options
// =============================================================================

/**
 * Object type dropdown options for PCF export
 * Based on PCF file structure - OBJECT-TYPE categorizes components for proper representation
 * Reference: User Manual Section 9.1 - OBJECT-TYPES vs REVIT components/parameters
 */
export const OBJECT_TYPE_OPTIONS: DropdownOption[] = [
  // Default parameter
  {
    value: '#PCF_OBJECT_TYPE',
    label: '#PCF_OBJECT_TYPE',
    tooltip: 'Default parameter added to pipes, pipe fittings, and pipe accessories. Required for PCF export.',
  },
  // General components
  {
    value: 'PIPE',
    label: 'PIPE',
    tooltip: 'System family for pipe segments.',
  },
  {
    value: 'ELBOW',
    label: 'ELBOW',
    tooltip: 'Elbow fitting for changing direction. Category: Pipe Fittings (Elbow). SKEYs: EL??, EB??',
  },
  {
    value: 'BEND',
    label: 'BEND',
    tooltip: 'Bend fitting including mitered bends. Category: Pipe Fittings (Elbow). SKEYs: PB??, BE??, MI??',
  },
  {
    value: 'TEE',
    label: 'TEE',
    tooltip: 'Tee fitting for branching pipe runs. Category: Pipe Fittings (Tee). SKEYs: TE??, TY??, BT??',
  },
  {
    value: 'TAP-CONNECTION',
    label: 'TAP-CONNECTION',
    tooltip: 'Tap connection fitting. Category: Pipe Fittings (Spud-Adjustable).',
  },
  {
    value: 'REDUCER_CONCENTRIC',
    label: 'REDUCER_CONCENTRIC',
    tooltip: 'Concentric reducer for changing pipe sizes with aligned centerlines. Category: Pipe Fittings (Transition). SKEYs: RC??, CPBW, CS??, RB??',
  },
  {
    value: 'REDUCER_ECCENTRIC',
    label: 'REDUCER_ECCENTRIC',
    tooltip: 'Eccentric reducer for changing pipe sizes with offset centerlines. Category: Pipe Fittings (Transition). SKEYs: RE??, ESBW',
  },
  {
    value: 'CAP',
    label: 'CAP',
    tooltip: 'Cap fitting for closing pipe ends. Category: Pipe Fittings (Cap). SKEYs: KABW, KASC, KASW, KAGL, KAFP',
  },
  {
    value: 'CROSS',
    label: 'CROSS',
    tooltip: 'Cross fitting with four connection points. Category: Pipe Fittings (Cross). SKEYs: CR??',
  },
  {
    value: 'UNION',
    label: 'UNION',
    tooltip: 'Union fitting for disconnectable joints. Category: Pipe Fittings (Union). SKEYs: UN??',
  },
  {
    value: 'COUPLING',
    label: 'COUPLING',
    tooltip: 'Coupling for connecting two pipes. Category: Pipe Fittings (Union). SKEYs: CO??, COSC, CSCP',
  },
  {
    value: 'OLET',
    label: 'OLET',
    tooltip: 'Olet branch connection (weldolet, threadolet, etc.). Category: Pipe Fittings (Tee). SKEYs: NI??, HC??, LA??, TH??, WTBW',
  },
  // Pipe Accessories
  {
    value: 'VALVE',
    label: 'VALVE',
    tooltip: 'Standard 2-way valve for controlling flow. Category: Pipe Accessories. SKEYs: VB??, VY??, CK??, VC??, VD??, VT??, VG??, NV??',
  },
  {
    value: 'VALVE-3WAY',
    label: 'VALVE-3WAY',
    tooltip: '3-way valve for diverting or mixing flow. Category: Pipe Accessories. SKEYs: V3??',
  },
  {
    value: 'VALVE-4WAY',
    label: 'VALVE-4WAY',
    tooltip: '4-way valve for complex flow control. Category: Pipe Accessories. SKEYs: V4??',
  },
  {
    value: 'VALVE-ANGLE',
    label: 'VALVE-ANGLE',
    tooltip: 'Angle valve with 90-degree flow path. Category: Pipe Accessories. SKEYs: AR??, CAFL, RAR??, AV??',
  },
  {
    value: 'INSTRUMENT',
    label: 'INSTRUMENT',
    tooltip: 'Flow meters, control valves, and instrumentation devices. Category: Pipe Accessories. SKEYs: II??, OP, PR, ITFL',
  },
  {
    value: 'FLANGE',
    label: 'FLANGE',
    tooltip: 'Flanged connection for bolted joints. Category: Pipe Accessories. SKEYs: FLSO, FOSO, FLSI, FLBL, FLSW, FLRC, FLWN, FOWN',
  },
  {
    value: 'FLANGE_BLIND',
    label: 'FLANGE_BLIND',
    tooltip: 'Blind flange for closing pipe ends. Category: Pipe Accessories. SKEY: FLBL',
  },
  {
    value: 'LAPJOINT-STUBEND',
    label: 'LAPJOINT-STUBEND',
    tooltip: 'Stub end for lap joint flanges. Category: Pipe Accessories. SKEY: FLSE. END-TYPE: LAP',
  },
  {
    value: 'GASKET',
    label: 'GASKET',
    tooltip: 'Gasket for sealing flanged connections. Also used as bolt set carrier. Category: Pipe Accessories.',
  },
  {
    value: 'BOLT',
    label: 'BOLT',
    tooltip: 'Bolt set for flanged connections. Created automatically or via gaskets. Category: Pipe Accessories.',
  },
  {
    value: 'SUPPORT',
    label: 'SUPPORT',
    tooltip: 'Pipe supports including anchors, guides, hangers, springs. Category: Pipe Accessories. SKEYs: SUPP, ANCH, GUID, SPRG, DUCK, HANG, SKID, PSIG, O1HG',
  },
  {
    value: 'FILTER',
    label: 'FILTER',
    tooltip: 'Filter or strainer component. Category: Pipe Accessories. SKEYs: FI??, FA??, FO??',
  },
  {
    value: 'MISC-COMPONENT',
    label: 'MISC-COMPONENT',
    tooltip: 'Miscellaneous components like expansion bellows, plugs, spectacle blinds. Category: Pipe Accessories. SKEYs: NC??, EX??, PL, BM, SB, SP, SR',
  },
  {
    value: 'WELD',
    label: 'WELD',
    tooltip: 'Weld point - automatically created by the plug-in between components.',
  },
  // ISO-related components
  {
    value: 'FLOW_ARROW',
    label: 'FLOW_ARROW',
    tooltip: 'ISO annotation: flow direction arrow symbol. SKEY: FLOW',
  },
  {
    value: 'FLOOR_SYMBOL',
    label: 'FLOOR_SYMBOL',
    tooltip: 'ISO annotation: floor penetration symbol. SKEY: FLOR',
  },
  {
    value: 'MESSAGE',
    label: 'MESSAGE',
    tooltip: 'ISO annotation: general message element.',
  },
  {
    value: 'MESSAGE_CIRCLE',
    label: 'MESSAGE_CIRCLE',
    tooltip: 'ISO annotation: circular message symbol.',
  },
  {
    value: 'MESSAGE_DIAMOND',
    label: 'MESSAGE_DIAMOND',
    tooltip: 'ISO annotation: diamond-shaped message symbol.',
  },
  {
    value: 'MESSAGE_ROUND',
    label: 'MESSAGE_ROUND',
    tooltip: 'ISO annotation: round message symbol.',
  },
  {
    value: 'ISO_SPLIT_POINT',
    label: 'ISO_SPLIT_POINT',
    tooltip: 'ISO annotation: drawing split point marker.',
  },
  {
    value: 'INSULATION_SYMBOL',
    label: 'INSULATION_SYMBOL',
    tooltip: 'ISO annotation: insulation indicator symbol.',
  },
  {
    value: 'LOCATION_POINT',
    label: 'LOCATION_POINT',
    tooltip: 'ISO annotation: location reference point.',
  },
  {
    value: 'START_CO_ORDS',
    label: 'START_CO_ORDS',
    tooltip: 'ISO annotation: starting coordinates marker.',
  },
  // Equipment
  {
    value: 'END_CONNECTION_EQUIPMENT',
    label: 'END_CONNECTION_EQUIPMENT',
    tooltip: 'Equipment nozzle connection point for mechanical equipment or generic models.',
  },
  {
    value: 'REINFORCEMENTPAD',
    label: 'REINFORCEMENTPAD',
    tooltip: 'Reinforcement pad for branch connections.',
  },
];

// =============================================================================
// SKey Options
// =============================================================================

/**
 * SKey (Symbol Key) dropdown options
 * SKEY tells the receiving software what the component is and how to represent it graphically
 * Reference: User Manual Section 9.2 - SKEY vs OBJECT TYPE
 */
export const SKEY_OPTIONS: DropdownOption[] = [
  // Default parameter
  {
    value: '#PCF_SKEY',
    label: '#PCF_SKEY',
    tooltip: 'Default SKEY parameter for pipes, pipe fittings, and pipe accessories. Defines the component symbol.',
  },
  // Elbow SKEYs
  {
    value: 'EL??',
    label: 'EL?? (Elbow)',
    tooltip: 'Standard elbow. ?? = angle (e.g., EL90, EL45). OBJECT-TYPE: ELBOW',
  },
  {
    value: 'EB??',
    label: 'EB?? (Elbow)',
    tooltip: 'Elbow alternate designation. ?? = angle. OBJECT-TYPE: ELBOW',
  },
  {
    value: 'ER??',
    label: 'ER?? (Reducing Elbow)',
    tooltip: 'Reducing elbow. ?? = angle. OBJECT-TYPE: ELBOW',
  },
  // Bend SKEYs
  {
    value: 'PB??',
    label: 'PB?? (Pipe Bend)',
    tooltip: 'Pipe bend. ?? = angle. OBJECT-TYPE: BEND',
  },
  {
    value: 'BE??',
    label: 'BE?? (Bend)',
    tooltip: 'Standard bend. ?? = angle. OBJECT-TYPE: BEND',
  },
  {
    value: 'MI??',
    label: 'MI?? (Mitered Bend)',
    tooltip: 'Mitered bend. ?? = angle. OBJECT-TYPE: BEND',
  },
  {
    value: 'EU??',
    label: 'EU?? (180 Elbow Return)',
    tooltip: '180-degree elbow return. OBJECT-TYPE: ELBOW',
  },
  {
    value: 'BU??',
    label: 'BU?? (180 Bend Return)',
    tooltip: '180-degree bend return. OBJECT-TYPE: BEND',
  },
  // Tee SKEYs
  {
    value: 'TE??',
    label: 'TE?? (Tee)',
    tooltip: 'Standard tee. ?? = type variation. OBJECT-TYPE: TEE',
  },
  {
    value: 'TY??',
    label: 'TY?? (Tee Y)',
    tooltip: 'Y-type tee. OBJECT-TYPE: TEE',
  },
  {
    value: 'BT??',
    label: 'BT?? (Tee Bend)',
    tooltip: 'Tee with bend. OBJECT-TYPE: TEE',
  },
  // Reducer SKEYs
  {
    value: 'RC??',
    label: 'RC?? (Reducer Concentric)',
    tooltip: 'Concentric reducer. OBJECT-TYPE: REDUCER_CONCENTRIC',
  },
  {
    value: 'CPBW',
    label: 'CPBW (Concentric BW)',
    tooltip: 'Concentric reducer butt-weld. OBJECT-TYPE: REDUCER_CONCENTRIC',
  },
  {
    value: 'CS??',
    label: 'CS?? (Concentric Swage)',
    tooltip: 'Concentric swage. OBJECT-TYPE: REDUCER_CONCENTRIC',
  },
  {
    value: 'RB??',
    label: 'RB?? (Reducer)',
    tooltip: 'General reducer. OBJECT-TYPE: REDUCER_CONCENTRIC',
  },
  {
    value: 'RE??',
    label: 'RE?? (Reducer Eccentric)',
    tooltip: 'Eccentric reducer. OBJECT-TYPE: REDUCER_ECCENTRIC',
  },
  {
    value: 'ESBW',
    label: 'ESBW (Eccentric BW)',
    tooltip: 'Eccentric reducer butt-weld. OBJECT-TYPE: REDUCER_ECCENTRIC',
  },
  // Cap SKEYs
  {
    value: 'KABW',
    label: 'KABW (Cap Butt-Weld)',
    tooltip: 'Cap with butt-weld end. OBJECT-TYPE: CAP',
  },
  {
    value: 'KASC',
    label: 'KASC (Cap Screwed)',
    tooltip: 'Screwed cap. OBJECT-TYPE: CAP',
  },
  {
    value: 'KASW',
    label: 'KASW (Cap Socket-Weld)',
    tooltip: 'Socket-weld cap. OBJECT-TYPE: CAP',
  },
  {
    value: 'KAGL',
    label: 'KAGL (Cap Glued)',
    tooltip: 'Glued cap. OBJECT-TYPE: CAP',
  },
  {
    value: 'KAFP',
    label: 'KAFP (Cap Flanged)',
    tooltip: 'Flanged cap. OBJECT-TYPE: CAP',
  },
  // Cross and Union SKEYs
  {
    value: 'CR??',
    label: 'CR?? (Cross)',
    tooltip: 'Cross fitting. OBJECT-TYPE: CROSS',
  },
  {
    value: 'UN??',
    label: 'UN?? (Union)',
    tooltip: 'Union fitting. OBJECT-TYPE: UNION',
  },
  {
    value: 'CO??',
    label: 'CO?? (Coupling)',
    tooltip: 'Standard coupling. OBJECT-TYPE: COUPLING',
  },
  {
    value: 'COSC',
    label: 'COSC (Coupling Screwed)',
    tooltip: 'Screwed coupling. OBJECT-TYPE: COUPLING',
  },
  {
    value: 'CSCP',
    label: 'CSCP (Compression Coupling)',
    tooltip: 'Compression coupling. OBJECT-TYPE: COUPLING',
  },
  // Flange SKEYs
  {
    value: 'FLSO',
    label: 'FLSO (Slip-On Flange)',
    tooltip: 'Slip-on flange. OBJECT-TYPE: FLANGE',
  },
  {
    value: 'FOSO',
    label: 'FOSO (Orifice Flange)',
    tooltip: 'Orifice flange. OBJECT-TYPE: FLANGE',
  },
  {
    value: 'FLSI',
    label: 'FLSI (Socket Insert Flange)',
    tooltip: 'Socket insert flange. OBJECT-TYPE: FLANGE',
  },
  {
    value: 'FLBL',
    label: 'FLBL (Blind Flange)',
    tooltip: 'Blind flange. OBJECT-TYPE: FLANGE_BLIND',
  },
  {
    value: 'FLSW',
    label: 'FLSW (Socket-Weld Flange)',
    tooltip: 'Socket-weld flange. OBJECT-TYPE: FLANGE',
  },
  {
    value: 'FLRC',
    label: 'FLRC (RTJ Flange)',
    tooltip: 'Ring type joint flange. OBJECT-TYPE: FLANGE',
  },
  {
    value: 'FLWN',
    label: 'FLWN (Weld Neck Flange)',
    tooltip: 'Weld neck flange. OBJECT-TYPE: FLANGE',
  },
  {
    value: 'FOWN',
    label: 'FOWN (Orifice Weld Neck)',
    tooltip: 'Orifice weld neck flange. OBJECT-TYPE: FLANGE',
  },
  {
    value: 'FLF',
    label: 'FLF (Fitting Flange)',
    tooltip: 'Fitting flange. OBJECT-TYPE: FLANGE',
  },
  {
    value: 'FLLB',
    label: 'FLLB (Lap Joint Flange)',
    tooltip: 'Lap joint flange. OBJECT-TYPE: FLANGE',
  },
  {
    value: 'FLSE',
    label: 'FLSE (Stub End)',
    tooltip: 'Stub end for lap joint. OBJECT-TYPE: LAPJOINT-STUBEND',
  },
  {
    value: 'FLGM',
    label: 'FLGM (Lined Flange)',
    tooltip: 'Lined flange. OBJECT-TYPE: FLANGE',
  },
  {
    value: 'FRGL',
    label: 'FRGL (Screwed Flange)',
    tooltip: 'Screwed flange. OBJECT-TYPE: FLANGE',
  },
  {
    value: 'FLFP',
    label: 'FLFP (Flat Face Flange)',
    tooltip: 'Flat face flange. OBJECT-TYPE: FLANGE',
  },
  {
    value: 'FLSC',
    label: 'FLSC (Threaded Flange)',
    tooltip: 'Threaded flange. OBJECT-TYPE: FLANGE',
  },
  {
    value: 'FLCL',
    label: 'FLCL (Clamped Flange)',
    tooltip: 'Clamped flange. OBJECT-TYPE: FLANGE',
  },
  // Valve SKEYs
  {
    value: 'VB??',
    label: 'VB?? (Ball Valve)',
    tooltip: 'Ball valve. OBJECT-TYPE: VALVE',
  },
  {
    value: 'VY??',
    label: 'VY?? (Butterfly Valve)',
    tooltip: 'Butterfly valve. OBJECT-TYPE: VALVE',
  },
  {
    value: 'ZB??',
    label: 'ZB?? (Butterfly Valve 2)',
    tooltip: 'Butterfly valve type 2. OBJECT-TYPE: VALVE',
  },
  {
    value: 'CK??',
    label: 'CK?? (Check Valve)',
    tooltip: 'Check valve. OBJECT-TYPE: VALVE',
  },
  {
    value: 'VC??',
    label: 'VC?? (Swing Check Valve)',
    tooltip: 'Swing check valve. OBJECT-TYPE: VALVE',
  },
  {
    value: 'VD??',
    label: 'VD?? (Diaphragm Valve)',
    tooltip: 'Diaphragm valve. OBJECT-TYPE: VALVE',
  },
  {
    value: 'VT??',
    label: 'VT?? (Gate Valve)',
    tooltip: 'Gate valve. OBJECT-TYPE: VALVE',
  },
  {
    value: 'VU??',
    label: 'VU?? (Gate Valve Alt)',
    tooltip: 'Gate valve alternate. OBJECT-TYPE: VALVE',
  },
  {
    value: 'CV??',
    label: 'CV?? (Control Valve)',
    tooltip: 'Control valve. OBJECT-TYPE: VALVE',
  },
  {
    value: 'VS??',
    label: 'VS?? (Slide Valve)',
    tooltip: 'Slide valve. OBJECT-TYPE: VALVE',
  },
  {
    value: 'VP??',
    label: 'VP?? (Plug Valve)',
    tooltip: 'Plug valve. OBJECT-TYPE: VALVE',
  },
  {
    value: 'VR??',
    label: 'VR?? (Regulating Valve)',
    tooltip: 'Regulating valve. OBJECT-TYPE: VALVE',
  },
  {
    value: 'VG??',
    label: 'VG?? (Globe Valve)',
    tooltip: 'Globe valve. OBJECT-TYPE: VALVE',
  },
  {
    value: 'NV??',
    label: 'NV?? (Needle Valve)',
    tooltip: 'Needle valve. OBJECT-TYPE: VALVE',
  },
  {
    value: 'V3??',
    label: 'V3?? (3-Way Valve)',
    tooltip: '3-way valve. OBJECT-TYPE: VALVE-3WAY',
  },
  {
    value: 'V4??',
    label: 'V4?? (4-Way Valve)',
    tooltip: '4-way valve. OBJECT-TYPE: VALVE-4WAY',
  },
  {
    value: 'AR??',
    label: 'AR?? (Angle Valve)',
    tooltip: 'Angle valve. OBJECT-TYPE: VALVE-ANGLE',
  },
  {
    value: 'CAFL',
    label: 'CAFL (Control Angle)',
    tooltip: 'Control angle valve. OBJECT-TYPE: VALVE-ANGLE',
  },
  {
    value: 'RAR??',
    label: 'RAR?? (Angle Relief)',
    tooltip: 'Angle relief valve. OBJECT-TYPE: VALVE-ANGLE',
  },
  {
    value: 'AV??',
    label: 'AV?? (Angle Valve Alt)',
    tooltip: 'Angle valve alternate. OBJECT-TYPE: VALVE-ANGLE',
  },
  // Support SKEYs
  {
    value: 'SUPP',
    label: 'SUPP (Support)',
    tooltip: 'General pipe support. OBJECT-TYPE: SUPPORT',
  },
  {
    value: 'O1HG',
    label: 'O1HG (General Support)',
    tooltip: 'General support designation. OBJECT-TYPE: SUPPORT',
  },
  {
    value: 'ANCH',
    label: 'ANCH (Anchor)',
    tooltip: 'Pipe anchor - fixed support point. OBJECT-TYPE: SUPPORT',
  },
  {
    value: 'GUID',
    label: 'GUID (Guide)',
    tooltip: 'Pipe guide - directional restraint. OBJECT-TYPE: SUPPORT',
  },
  {
    value: 'SPRG',
    label: 'SPRG (Spring)',
    tooltip: 'Spring hanger support. OBJECT-TYPE: SUPPORT',
  },
  {
    value: 'DUCK',
    label: 'DUCK (Dummy Leg)',
    tooltip: 'Dummy leg support. OBJECT-TYPE: SUPPORT',
  },
  {
    value: 'HANG',
    label: 'HANG (Hanger)',
    tooltip: 'Pipe hanger. OBJECT-TYPE: SUPPORT',
  },
  {
    value: 'SKID',
    label: 'SKID (Skid)',
    tooltip: 'Skid support. OBJECT-TYPE: SUPPORT',
  },
  {
    value: 'PSIG',
    label: 'PSIG (Support on Support)',
    tooltip: 'Support on support. OBJECT-TYPE: SUPPORT',
  },
  // Olet SKEYs
  {
    value: 'NI??',
    label: 'NI?? (Nip-o-let)',
    tooltip: 'Nip-o-let branch connection. OBJECT-TYPE: OLET',
  },
  {
    value: 'HC??',
    label: 'HC?? (Half-Coupling Olet)',
    tooltip: 'Half-coupling olet. OBJECT-TYPE: OLET',
  },
  {
    value: 'LA??',
    label: 'LA?? (Latrolet)',
    tooltip: 'Latrolet branch connection. OBJECT-TYPE: OLET',
  },
  {
    value: 'TH??',
    label: 'TH?? (Threadolet)',
    tooltip: 'Threadolet branch. OBJECT-TYPE: OLET',
  },
  {
    value: 'WTBW',
    label: 'WTBW (Weldolet)',
    tooltip: 'Weldolet branch. OBJECT-TYPE: OLET',
  },
  {
    value: 'SKSW',
    label: 'SKSW (Sockolet)',
    tooltip: 'Sockolet branch. OBJECT-TYPE: OLET',
  },
  // Instrument SKEYs
  {
    value: 'II??',
    label: 'II?? (Inline Instrument)',
    tooltip: 'Inline instrument. OBJECT-TYPE: INSTRUMENT',
  },
  {
    value: 'OP',
    label: 'OP (Orifice Plate)',
    tooltip: 'Orifice plate for flow measurement. OBJECT-TYPE: INSTRUMENT',
  },
  {
    value: 'PR',
    label: 'PR (Pressure)',
    tooltip: 'Pressure instrument. OBJECT-TYPE: INSTRUMENT',
  },
  {
    value: 'ITFL',
    label: 'ITFL (Instrument Tee)',
    tooltip: 'Instrument tee. OBJECT-TYPE: INSTRUMENT',
  },
  // Filter SKEYs
  {
    value: 'FI??',
    label: 'FI?? (Filter Strainer)',
    tooltip: 'Filter/strainer. OBJECT-TYPE: FILTER',
  },
  {
    value: 'FA??',
    label: 'FA?? (Angle Filter)',
    tooltip: 'Angle filter. OBJECT-TYPE: FILTER',
  },
  {
    value: 'FO??',
    label: 'FO?? (Filter with Offset)',
    tooltip: 'Filter with offset. OBJECT-TYPE: FILTER',
  },
  // Misc SKEYs
  {
    value: 'NC??',
    label: 'NC?? (Non-Catalog Item)',
    tooltip: 'Non-catalog miscellaneous item. OBJECT-TYPE: MISC-COMPONENT',
  },
  {
    value: 'EX??',
    label: 'EX?? (Expansion Bellows)',
    tooltip: 'Expansion bellows/joint. OBJECT-TYPE: MISC-COMPONENT',
  },
  {
    value: 'PL',
    label: 'PL (Plug)',
    tooltip: 'Pipe plug. OBJECT-TYPE: MISC-COMPONENT',
  },
  {
    value: 'BM',
    label: 'BM (Blanking Plug)',
    tooltip: 'Male blanking plug. OBJECT-TYPE: MISC-COMPONENT',
  },
  {
    value: 'SB',
    label: 'SB (Open Spectacle Blind)',
    tooltip: 'Open spectacle blind. OBJECT-TYPE: MISC-COMPONENT',
  },
  {
    value: 'SP',
    label: 'SP (Spectacle Blind)',
    tooltip: 'Spectacle blind. OBJECT-TYPE: MISC-COMPONENT',
  },
  {
    value: 'SR',
    label: 'SR (Spacer)',
    tooltip: 'Spacer. OBJECT-TYPE: MISC-COMPONENT',
  },
  // ISO SKEYs
  {
    value: 'FLOW',
    label: 'FLOW (Flow Arrow)',
    tooltip: 'Flow direction arrow. OBJECT-TYPE: FLOW_ARROW',
  },
  {
    value: 'FLOR',
    label: 'FLOR (Floor Symbol)',
    tooltip: 'Floor penetration symbol. OBJECT-TYPE: FLOOR_SYMBOL',
  },
  // Ferrule
  {
    value: 'FE??',
    label: 'FE?? (Ferrule Flared)',
    tooltip: 'Flared ferrule. END-TYPE: TC (tube clamp)',
  },
  // Nipple
  {
    value: 'NR??',
    label: 'NR?? (Nipple)',
    tooltip: 'Pipe nipple. OBJECT-TYPE: COUPLING',
  },
  {
    value: 'NB??',
    label: 'NB?? (Nipple BS)',
    tooltip: 'British standard nipple. OBJECT-TYPE: COUPLING',
  },
  // Trap
  {
    value: 'TI??',
    label: 'TI?? (Trap)',
    tooltip: 'Steam trap or drain trap.',
  },
  // Adapter
  {
    value: 'ADMM',
    label: 'ADMM (M-M Adapter)',
    tooltip: 'Male to male adapter.',
  },
  {
    value: 'ADMF',
    label: 'ADMF (M-F Adapter)',
    tooltip: 'Male to female adapter.',
  },
];

// =============================================================================
// Piping Spec Options
// =============================================================================

/**
 * Piping specification dropdown options
 */
export const PIPING_SPEC_OPTIONS: DropdownOption[] = [
  {
    value: 'PiP_PPG_SPEC',
    label: 'PiP_PPG_SPEC',
    tooltip: 'Default parameter added to pipe, pipe fitting, and pipe accessory families for PCF export.',
  },
  {
    value: 'STD',
    label: 'STD',
    tooltip: 'Standard piping specification (PIPING-SPEC: DUMMY, 10CS21, 16SS21, etc.)',
  },
  {
    value: 'SCH40',
    label: 'SCH40',
    tooltip: 'Schedule 40 piping specification (PIPING-SPEC: DUMMY, 10CS21, 16SS21, etc.)',
  },
  {
    value: 'SCH80',
    label: 'SCH80',
    tooltip: 'Schedule 80 piping specification (PIPING-SPEC: DUMMY, 10CS21, 16SS21, etc.)',
  },
  {
    value: 'SCH160',
    label: 'SCH160',
    tooltip: 'Schedule 160 piping specification (PIPING-SPEC: DUMMY, 10CS21, 16SS21, etc.)',
  },
];

// =============================================================================
// System Grouping Options
// =============================================================================

/**
 * System grouping dropdown options for export
 */
export const SYSTEM_GROUPING_OPTIONS: DropdownOption[] = [
  {
    value: 'BySystemName',
    label: 'BySystemName',
    tooltip: 'Export by system name. Best for ISO creation when system names are maintained carefully.',
  },
  {
    value: 'BySystemTypeName',
    label: 'BySystemTypeName',
    tooltip: 'Export by system type. Best for bulk exports such as stress or pressure-drop workflows.',
  },
];

// =============================================================================
// End Type Options (Connection Types)
// =============================================================================

/**
 * END-TYPE dropdown options for connector endpoints
 * Defines how components connect to each other (welded, flanged, threaded, etc.)
 * Reference: User Manual Section 9.3 - END-TYPES
 * Port mapping: port1=primary (IsPrimary), port2=secondary (opposite), port3=perpendicular, port4+=numbered in description with #
 */
export const END_TYPE_OPTIONS: DropdownOption[] = [
  // Default parameter
  {
    value: 'PiX_PPG_END1_TYPE',
    label: 'PiX_PPG_END1_TYPE',
    tooltip: 'Default END-TYPE parameter. Defines connection method (welded, flanged, threaded, etc.).',
  },
  // Weld types
  {
    value: 'WF',
    label: 'WF (Field Weld)',
    tooltip: 'Field weld connection - welded on site during construction.',
  },
  {
    value: 'WS',
    label: 'WS (Field Weld)',
    tooltip: 'Field weld connection alternate designation.',
  },
  {
    value: 'WW',
    label: 'WW (Weld)',
    tooltip: 'Shop weld connection.',
  },
  {
    value: 'SO',
    label: 'SO (Weld)',
    tooltip: 'Slip-on weld connection.',
  },
  {
    value: 'WN',
    label: 'WN (Weld Neck)',
    tooltip: 'Weld neck connection.',
  },
  {
    value: 'SJ',
    label: 'SJ (Weld)',
    tooltip: 'Socket joint weld.',
  },
  {
    value: 'BW',
    label: 'BW (Butt Weld)',
    tooltip: 'Butt weld connection - ends welded directly together.',
  },
  {
    value: 'BV',
    label: 'BV (Butt Weld)',
    tooltip: 'Butt weld (bevel) connection - most common for pipe fittings.',
  },
  {
    value: 'SW',
    label: 'SW (Socket Weld)',
    tooltip: 'Socket weld connection - pipe inserted into socket and welded.',
  },
  // Flanged connections
  {
    value: 'FL',
    label: 'FL (Flanged)',
    tooltip: 'Flanged connection - bolted flange joint.',
  },
  {
    value: 'LFL',
    label: 'LFL (Lined Flange)',
    tooltip: 'Lined flange connection.',
  },
  {
    value: 'LAP',
    label: 'LAP (Lap Joint)',
    tooltip: 'Lap joint connection - used with stub ends.',
  },
  // Threaded connections
  {
    value: 'SC',
    label: 'SC (Threaded/Screwed)',
    tooltip: 'Threaded/screwed connection.',
  },
  // Other connections
  {
    value: 'GL',
    label: 'GL (Glued)',
    tooltip: 'Glued connection - common for plastic piping.',
  },
  {
    value: 'CJ',
    label: 'CJ (Clamped)',
    tooltip: 'Clamped connection - mechanical clamp joint.',
  },
  {
    value: 'TC',
    label: 'TC (Tube Clamp)',
    tooltip: 'Tube clamp connection - for ferrules and tubing.',
  },
  {
    value: 'JT',
    label: 'JT (End Flared)',
    tooltip: 'End flared connection.',
  },
  {
    value: 'A1',
    label: 'A1 (End Flared)',
    tooltip: 'End flared connection alternate designation.',
  },
  {
    value: 'CP',
    label: 'CP (Compression)',
    tooltip: 'Compression fitting connection.',
  },
  // Special/undefined
  {
    value: 'UNDEFINED_ET',
    label: 'UNDEFINED_ET',
    tooltip: 'Undefined end type - used for supports, gaskets, and non-piping components.',
  },
];

// Legacy alias for backward compatibility
export const ENDPOINT_OPTIONS = END_TYPE_OPTIONS;

// =============================================================================
// Material Options
// =============================================================================

/**
 * Material dropdown options
 */
export const MATERIAL_OPTIONS: DropdownOption[] = [
  {
    value: 'PiX_03_Material_ENG',
    label: 'PiX_03_Material_ENG',
    tooltip: 'Material field used in the bill of materials.',
  },
  {
    value: 'STEEL',
    label: 'STEEL',
    tooltip: 'Steel material specification (material examples: steel, stainless steel, duplex, etc.)',
  },
  {
    value: 'STAINLESS_STEEL',
    label: 'STAINLESS STEEL',
    tooltip: 'Stainless steel material specification (material examples: steel, stainless steel, duplex, etc.)',
  },
  {
    value: 'CARBON_STEEL',
    label: 'CARBON STEEL',
    tooltip: 'Carbon steel material specification (material examples: steel, stainless steel, duplex, etc.)',
  },
  {
    value: 'COPPER',
    label: 'COPPER',
    tooltip: 'Copper material specification (material examples: steel, stainless steel, duplex, etc.)',
  },
];

// =============================================================================
// Design Standard Options
// =============================================================================

/**
 * Design standard dropdown options
 */
export const DESIGN_STANDARD_OPTIONS: DropdownOption[] = [
  {
    value: 'PiX_02_Standard_ENG',
    label: 'PiX_02_Standard_ENG',
    tooltip: 'Design standard field used in the bill of materials.',
  },
  {
    value: 'ASME_B31.1',
    label: 'ASME B31.1',
    tooltip: 'Power piping standard (DesignStd: ASME 16.3, EN 1092-1, etc.)',
  },
  {
    value: 'ASME_B31.3',
    label: 'ASME B31.3',
    tooltip: 'Process piping standard (DesignStd: ASME 16.3, EN 1092-1, etc.)',
  },
  {
    value: 'ASME_B31.4',
    label: 'ASME B31.4',
    tooltip: 'Pipeline transportation systems (DesignStd: ASME 16.3, EN 1092-1, etc.)',
  },
  {
    value: 'ASME_B31.8',
    label: 'ASME B31.8',
    tooltip: 'Gas transmission and distribution piping (DesignStd: ASME 16.3, EN 1092-1, etc.)',
  },
];

// =============================================================================
// Pressure Class Options
// =============================================================================

/**
 * Pressure class dropdown options
 */
export const PRESSURE_CLASS_OPTIONS: DropdownOption[] = [
  {
    value: 'PiX_PPG_END1_PressureRating',
    label: 'PiX_PPG_END1_PressureRating',
    tooltip: 'Pressure rating field used in the bill of materials.',
  },
  {
    value: 'CLASS_150',
    label: 'CLASS 150',
    tooltip: 'Pressure rating of 150 PSI (PressureClass: 10, 16, 25, 40, etc.)',
  },
  {
    value: 'CLASS_300',
    label: 'CLASS 300',
    tooltip: 'Pressure rating of 300 PSI (PressureClass: 10, 16, 25, 40, etc.)',
  },
  {
    value: 'CLASS_600',
    label: 'CLASS 600',
    tooltip: 'Pressure rating of 600 PSI (PressureClass: 10, 16, 25, 40, etc.)',
  },
  {
    value: 'CLASS_900',
    label: 'CLASS 900',
    tooltip: 'Pressure rating of 900 PSI (PressureClass: 10, 16, 25, 40, etc.)',
  },
  {
    value: 'CLASS_1500',
    label: 'CLASS 1500',
    tooltip: 'Pressure rating of 1500 PSI (PressureClass: 10, 16, 25, 40, etc.)',
  },
];

// =============================================================================
// Insulation Options
// =============================================================================

/**
 * Insulation specification dropdown options
 */
export const INSULATION_SPEC_OPTIONS: DropdownOption[] = [
  {
    value: 'PiX_GN_2_InsulationSpec',
    label: 'PiX_GN_2_InsulationSpec',
    tooltip: 'Insulation specification type',
  },
  {
    value: 'STD',
    label: 'STD',
    tooltip: 'Standard insulation specification. Example INSULATION-SPEC values include PP and HC.',
  },
  {
    value: 'HT',
    label: 'HT',
    tooltip: 'High-temperature insulation specification. Example INSULATION-SPEC values include PP and HC.',
  },
  {
    value: 'ACM',
    label: 'ACM',
    tooltip: 'Insulation specification for asbestos-containing material.',
  },
  {
    value: 'NON_ACM',
    label: 'NON ACM',
    tooltip: 'Insulation specification for non-asbestos material.',
  },
];

/**
 * Insulation thickness dropdown options
 */
export const INSULATION_THICKNESS_OPTIONS: DropdownOption[] = [
  {
    value: 'PiX_GN_2_InsulationThickness',
    label: 'PiX_GN_2_InsulationThickness',
    tooltip: 'Insulation thickness specification',
  },
  {
    value: '25MM',
    label: '25MM',
    tooltip: '25 mm insulation thickness (INSULATIONTHICKNESS: 40, 50, 60, 70, etc.)',
  },
  {
    value: '50MM',
    label: '50MM',
    tooltip: '50 mm insulation thickness (INSULATIONTHICKNESS: 40, 50, 60, 70, etc.)',
  },
  {
    value: '75MM',
    label: '75MM',
    tooltip: '75 mm insulation thickness (INSULATIONTHICKNESS: 40, 50, 60, 70, etc.)',
  },
  {
    value: '100MM',
    label: '100MM',
    tooltip: '100 mm insulation thickness (INSULATIONTHICKNESS: 40, 50, 60, 70, etc.)',
  },
];

// =============================================================================
// Tracing Options
// =============================================================================

/**
 * Heat tracing specification dropdown options
 */
export const TRACING_SPEC_OPTIONS: DropdownOption[] = [
  {
    value: 'PiX_GN_2_HeatTracingSpec',
    label: 'PiX_GN_2_HeatTracingSpec',
    tooltip: 'Heat tracing specification',
  },
  {
    value: 'ELECTRIC',
    label: 'ELECTRIC',
    tooltip: 'Electric heat tracing specification (TRACING-SPEC: Steam, Electric, etc.)',
  },
  {
    value: 'STEAM',
    label: 'STEAM',
    tooltip: 'Steam heat tracing specification (TRACING-SPEC: Steam, Electric, etc.)',
  },
  {
    value: 'NONE',
    label: 'NONE',
    tooltip: 'No heat tracing specification (TRACING-SPEC: Steam, Electric, etc.)',
  },
  {
    value: 'CONSTANT_WATT',
    label: 'CONSTANT WATT',
    tooltip: 'Constant wattage heat tracing specification (TRACING-SPEC: Steam, Electric, etc.)',
  },
];

// =============================================================================
// Item Description Options
// =============================================================================

/**
 * Item description dropdown options
 */
export const ITEM_DESCRIPTION_OPTIONS: DropdownOption[] = [
  {
    value: 'PiX_01_Description_ENG',
    label: 'PiX_01_Description_ENG',
    tooltip: 'English item description.',
  },
  {
    value: 'PIPE_SECTION',
    label: 'PIPE SECTION',
    tooltip: 'Pipe section description (ITEM-DESCRIPTION example: elbow LR 90-degree, etc.)',
  },
  {
    value: 'FITTING',
    label: 'FITTING',
    tooltip: 'Fitting description (ITEM-DESCRIPTION example: elbow LR 90-degree, etc.)',
  },
  {
    value: 'VALVE',
    label: 'VALVE',
    tooltip: 'Valve description (ITEM-DESCRIPTION example: elbow LR 90-degree, etc.)',
  },
  {
    value: 'FLANGE',
    label: 'FLANGE',
    tooltip: 'Flange description (ITEM-DESCRIPTION example: elbow LR 90-degree, etc.)',
  },
];

// =============================================================================
// Process Conditions Options
// =============================================================================

/**
 * Process conditions dropdown options
 */
export const PROCESS_CONDITIONS_OPTIONS: DropdownOption[] = [
  {
    value: 'Temperature',
    label: 'Temperature',
    tooltip: 'Temperature-related process condition, such as design or operating temperature.',
  },
  {
    value: 'Pressure',
    label: 'Pressure',
    tooltip: 'Pressure-related process condition, such as design or operating pressure.',
  },
  {
    value: 'FlowRate',
    label: 'Flow rate',
    tooltip: 'Flow-rate process condition.',
  },
  {
    value: 'Density',
    label: 'Density',
    tooltip: 'Density-related process condition.',
  },
  {
    value: 'Viscosity',
    label: 'Viscosity',
    tooltip: 'Viscosity-related process condition.',
  },
];

// =============================================================================
// Project Properties Options
// =============================================================================

/**
 * Project properties dropdown options
 */
export const PROJECT_PROPERTIES_OPTIONS: DropdownOption[] = [
  {
    value: 'Insulation',
    label: 'Insulation',
    tooltip: 'Project property: insulation.',
  },
  {
    value: 'Coating',
    label: 'Coating',
    tooltip: 'Project property: coating.',
  },
  {
    value: 'Support',
    label: 'Support',
    tooltip: 'Project property: support (for example, SUPP, ANCH, GUID, or SPRG).',
  },
  {
    value: 'Specification',
    label: 'Specification',
    tooltip: 'Project property: specification.',
  },
  {
    value: 'DrawingNumber',
    label: 'Drawing number',
    tooltip: 'Project property: drawing number.',
  },
];

// =============================================================================
// Item Tag Options
// =============================================================================

/**
 * Item tag dropdown options
 */
export const ITEM_TAG_OPTIONS: DropdownOption[] = [
  {
    value: 'PiX_GN_4_ItemTag',
    label: 'PiX_GN_4_ItemTag',
    tooltip: 'Tag used to identify a component.',
  },
  {
    value: 'VALVE_TAG',
    label: 'VALVE_TAG',
    tooltip: 'Valve component tag',
  },
  {
    value: 'SUPPORT_TAG',
    label: 'SUPPORT_TAG',
    tooltip: 'Support component tag',
  },
  {
    value: 'INSTRUMENT_TAG',
    label: 'INSTRUMENT_TAG',
    tooltip: 'Instrument component tag',
  },
  {
    value: 'PIPE_TAG',
    label: 'PIPE_TAG',
    tooltip: 'Pipe component tag',
  },
];

// =============================================================================
// Support Structure Reference Options
// =============================================================================

/**
 * Support structure reference dropdown options
 */
export const SS_REFERENCE_OPTIONS: DropdownOption[] = [
  {
    value: 'SSReference',
    label: 'SSReference',
    tooltip: 'Reference to the support structure.',
  },
  {
    value: 'SUPPORT_1',
    label: 'SUPPORT_1',
    tooltip: 'First support reference.',
  },
  {
    value: 'SUPPORT_2',
    label: 'SUPPORT_2',
    tooltip: 'Second support reference.',
  },
  {
    value: 'SUPPORT_3',
    label: 'SUPPORT_3',
    tooltip: 'Third support reference.',
  },
  {
    value: 'SUPPORT_4',
    label: 'SUPPORT_4',
    tooltip: 'Fourth support reference.',
  },
];

// =============================================================================
// Attribute Type Options
// =============================================================================

/**
 * Attribute type dropdown options for mapping table
 */
export const ATTRIBUTE_TYPE_OPTIONS: DropdownOption[] = [
  {
    value: 'Parameter',
    label: 'Parameter',
    tooltip: 'Revit parameter type',
  },
  {
    value: 'BomColumn',
    label: 'BomColumn',
    tooltip: 'Bill of materials column.',
  },
  {
    value: 'Attribute',
    label: 'Attribute',
    tooltip: 'Generic attribute type',
  },
];

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Find an option by value in a dropdown options array
 * @param options - The dropdown options array to search
 * @param value - The value to find
 * @returns The matching option or undefined
 */
export function findOptionByValue(
  options: DropdownOption[],
  value: string
): DropdownOption | undefined {
  return options.find((option) => option.value === value);
}

/**
 * Get just the values from a dropdown options array
 * @param options - The dropdown options array
 * @returns Array of values
 */
export function getOptionValues(options: DropdownOption[]): string[] {
  return options.map((option) => option.value);
}

/**
 * Get just the labels from a dropdown options array
 * @param options - The dropdown options array
 * @returns Array of labels
 */
export function getOptionLabels(options: DropdownOption[]): string[] {
  return options.map((option) => option.label);
}
